import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { protestService, governorateService, categoryService, sourceService, storyService } from '../services/api';
import PhotoUpload from '../components/PhotoUpload';
import RichTextEditor from '../components/RichTextEditor';
import styles from './AddProtestManual.module.css';

const PROTEST_TYPES = ['مظاهرة', 'إضراب', 'تجمع', 'وقفة احتجاجية', 'اعتصام', 'مسيرة', 'أخرى'];

const POLITICAL_ORIENTATIONS = [
    { value: '',                    label: '— غير محدد —' },
    { value: 'neutral_undefined',   label: 'محايد/غير محدد' },
    { value: 'government_supporter',label: 'مؤيد للحكومة' },
    { value: 'government_opponent', label: 'معارض للحكومة' },
    { value: 'sectarian',           label: 'طائفي' },
    { value: 'separatist',          label: 'انفصالي' },
    { value: 'riots',               label: 'أعمال شغب' },
];

const SOURCE_TYPES = [
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter',  label: 'Twitter/X' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'news',     label: 'موقع إخباري' },
    { value: 'video',    label: 'فيديو' },
    { value: 'other',    label: 'أخرى' },
];

const EMPTY = {
    governorate_id: '',
    region_id: '',
    protest_date: new Date().toISOString().split('T')[0],
    protest_type: 'مظاهرة',
    estimated_participants: '',
    demands: '',
    category_id: '',
    sub_category_id: '',
    political_orientation: '',
    verified: false,
    source_url: '',
    photos: [],
};

export default function AddProtestManual() {
    const navigate = useNavigate();
    const [step,         setStep]         = useState(1);
    const [form,         setForm]         = useState(EMPTY);
    const [governorates, setGovernorates] = useState([]);
    const [regions,      setRegions]      = useState([]);
    const [categories,   setCategories]   = useState([]);
    const [sources,      setSources]      = useState([]);
    const [stories,      setStories]      = useState([]);
    const [newSource,    setNewSource]    = useState({ source_url: '', source_type: 'news' });
    const [newStory,     setNewStory]     = useState({ title: '', content: '' });
    const [loading,      setLoading]      = useState(false);
    const [message,      setMessage]      = useState(null);

    useEffect(() => {
        governorateService.list().then((r) => setGovernorates(r.data)).catch(console.error);
        categoryService.list().then((r) => setCategories(r.data)).catch(console.error);
    }, []);

    const handleGovernorateChange = async (govId) => {
        setForm((f) => ({ ...f, governorate_id: govId, region_id: '' }));
        setRegions([]);
        if (!govId) return;
        try {
            const r = await governorateService.regions(govId);
            setRegions(r.data);
        } catch (e) { console.error(e); }
    };

    const subCategories = categories.find((c) => c.id === parseInt(form.category_id))?.sub_categories || [];

    const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    // ── Sources ──
    const addSource = () => {
        if (!newSource.source_url.trim()) return;
        setSources((s) => [...s, { ...newSource, _id: Date.now() }]);
        setNewSource({ source_url: '', source_type: 'news' });
    };

    // ── Stories ──
    const addStory = () => {
        if (!newStory.title.trim() || !newStory.content.trim()) return;
        setStories((s) => [...s, { ...newStory, _id: Date.now() }]);
        setNewStory({ title: '', content: '' });
    };

    // ── Submit ──
    const handleSubmit = async () => {
        setLoading(true);
        setMessage(null);

        const payload = new FormData();
        payload.append('governorate_id', form.governorate_id);
        payload.append('region_id', form.region_id);
        payload.append('protest_date', form.protest_date);
        payload.append('protest_type', form.protest_type);
        if (form.estimated_participants) payload.append('estimated_participants', form.estimated_participants);
        if (form.demands)                payload.append('demands', form.demands);
        if (form.category_id)            payload.append('category_id', form.category_id);
        if (form.sub_category_id)        payload.append('sub_category_id', form.sub_category_id);
        if (form.political_orientation)  payload.append('political_orientation', form.political_orientation);
        if (form.source_url)             payload.append('source_url', form.source_url);
        payload.append('verified', form.verified ? '1' : '0');
        form.photos.forEach((file, i) => payload.append(`photos[${i}]`, file));

        try {
            const res = await protestService.createManual(payload);
            const protestId = res.data.id;

            // Add sources
            for (const src of sources) {
                await sourceService.create(protestId, { source_url: src.source_url, source_type: src.source_type });
            }

            // Add stories
            for (const story of stories) {
                await storyService.create(protestId, { title: story.title, content: story.content });
            }

            setMessage({ type: 'success', text: 'تم إضافة التظاهرة بنجاح ✓' });
            setForm(EMPTY);
            setRegions([]);
            setSources([]);
            setStories([]);
            setStep(1);
        } catch (err) {
            const errors = err.response?.data?.errors;
            setMessage({
                type: 'error',
                text: errors
                    ? Object.values(errors).flat().join(' | ')
                    : 'خطأ: ' + (err.response?.data?.message || err.message),
            });
        } finally {
            setLoading(false);
        }
    };

    // ─────────────────────────────── STEP 1 ────────────────────────────────
    if (step === 1) return (
        <div className={`${styles.container} text-ar`}>
            <div className={styles.header}>
                <h1>إضافة احتجاج (نموذج يدوي)</h1>
                <p>أدخل بيانات التظاهرة مباشرة بدون استخدام الذكاء الاصطناعي</p>
            </div>

            {message && <div className={`${styles.alert} ${styles[message.type]}`}>{message.text}</div>}

            <form onSubmit={(e) => { e.preventDefault(); setStep(2); }} className={styles.form}>
                <div className={styles.grid2}>
                    {/* Governorate */}
                    <div className={styles.field}>
                        <label className={styles.label}>المحافظة *</label>
                        <select className={styles.input} value={form.governorate_id}
                            onChange={(e) => handleGovernorateChange(e.target.value)} required>
                            <option value="">اختر المحافظة</option>
                            {governorates.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                    </div>

                    {/* Region */}
                    <div className={styles.field}>
                        <label className={styles.label}>المنطقة *</label>
                        <select className={styles.input} value={form.region_id}
                            onChange={(e) => set('region_id', e.target.value)} required disabled={!form.governorate_id}>
                            <option value="">اختر المنطقة</option>
                            {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </div>

                    {/* Date */}
                    <div className={styles.field}>
                        <label className={styles.label}>التاريخ *</label>
                        <input type="date" className={styles.input} value={form.protest_date}
                            onChange={(e) => set('protest_date', e.target.value)} required />
                    </div>

                    {/* Type */}
                    <div className={styles.field}>
                        <label className={styles.label}>نوع الحدث *</label>
                        <select className={styles.input} value={form.protest_type}
                            onChange={(e) => set('protest_type', e.target.value)} required>
                            {PROTEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    {/* Participants */}
                    <div className={styles.field}>
                        <label className={styles.label}>عدد المشاركين المتوقع</label>
                        <input type="number" className={styles.input} value={form.estimated_participants}
                            onChange={(e) => set('estimated_participants', e.target.value)} min="0" placeholder="0" />
                    </div>

                    {/* Political Orientation */}
                    <div className={styles.field}>
                        <label className={styles.label}>التوجه السياسي</label>
                        <select className={styles.input} value={form.political_orientation}
                            onChange={(e) => set('political_orientation', e.target.value)}>
                            {POLITICAL_ORIENTATIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Category */}
                    <div className={styles.field}>
                        <label className={styles.label}>التصنيف</label>
                        <select className={styles.input} value={form.category_id}
                            onChange={(e) => set('category_id', e.target.value)}>
                            <option value="">— غير محدد —</option>
                            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>

                    {/* Sub-category */}
                    {form.category_id && (
                        <div className={styles.field}>
                            <label className={styles.label}>التصنيف الفرعي</label>
                            <select className={styles.input} value={form.sub_category_id}
                                onChange={(e) => set('sub_category_id', e.target.value)}>
                                <option value="">— غير محدد —</option>
                                {subCategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                    )}
                </div>

                {/* Demands */}
                <div className={styles.field}>
                    <label className={styles.label}>المطالب والأهداف</label>
                    <textarea className={styles.textarea} value={form.demands}
                        onChange={(e) => set('demands', e.target.value)}
                        placeholder="المطالب الرئيسية للاحتجاج..." rows={4} />
                </div>

                {/* Source URL */}
                <div className={styles.field}>
                    <label className={styles.label}>رابط المصدر الرئيسي <span className={styles.optional}>(اختياري)</span></label>
                    <input type="url" className={styles.input} value={form.source_url}
                        onChange={(e) => set('source_url', e.target.value)} placeholder="https://..." />
                </div>

                {/* Photos */}
                <div className={styles.field}>
                    <label className={styles.label}>الصور <span className={styles.optional}>(اختياري)</span></label>
                    <PhotoUpload photos={form.photos} multiple onChange={(files) => set('photos', files)} />
                </div>

                {/* Verified */}
                <label className={styles.checkLabel}>
                    <input type="checkbox" checked={form.verified}
                        onChange={(e) => set('verified', e.target.checked)} />
                    <span>تم رصده ميدانياً (موثق)</span>
                </label>

                <div className={styles.stepActions}>
                    <button type="submit" disabled={!form.governorate_id || !form.region_id} className={styles.nextBtn}>
                        التالي ← (إضافة مصادر وقصص)
                    </button>
                </div>
            </form>
        </div>
    );

    // ─────────────────────────────── STEP 2 ────────────────────────────────
    return (
        <div className={`${styles.container} text-ar`}>
            <div className={styles.header}>
                <h1>إضافة احتجاج — المصادر والقصص</h1>
                <p>يمكنك إضافة مصادر وقصص ذات صلة بالاحتجاج (اختياري)</p>
            </div>

            {message && <div className={`${styles.alert} ${styles[message.type]}`}>{message.text}</div>}

            <div className={styles.twoCol}>
                {/* SOURCES */}
                <section className={styles.card}>
                    <h2 className={styles.cardTitle}>المصادر</h2>
                    <p className={styles.cardHint}>أضف روابط المصادر لهذا الاحتجاج</p>

                    <div className={styles.sourceRow}>
                        <input type="url" className={styles.input} placeholder="رابط المصدر"
                            value={newSource.source_url}
                            onChange={(e) => setNewSource((s) => ({ ...s, source_url: e.target.value }))} />
                        <select className={styles.inputSm} value={newSource.source_type}
                            onChange={(e) => setNewSource((s) => ({ ...s, source_type: e.target.value }))}>
                            {SOURCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <button type="button" className={styles.addBtn} onClick={addSource}>+ إضافة</button>
                    </div>

                    <div className={styles.list}>
                        {sources.length === 0 && <p className={styles.empty}>لا توجد مصادر مضافة</p>}
                        {sources.map((src) => (
                            <div key={src._id} className={styles.listItem}>
                                <div>
                                    <a href={src.source_url} target="_blank" rel="noreferrer" className={styles.sourceLink}>
                                        {src.source_url.length > 50 ? src.source_url.slice(0, 50) + '…' : src.source_url}
                                    </a>
                                    <span className={styles.badge}>{src.source_type}</span>
                                </div>
                                <button className={styles.removeBtn}
                                    onClick={() => setSources((s) => s.filter((x) => x._id !== src._id))}>✕</button>
                            </div>
                        ))}
                    </div>
                </section>

                {/* STORIES */}
                <section className={styles.card}>
                    <h2 className={styles.cardTitle}>القصص</h2>
                    <p className={styles.cardHint}>أضف قصصاً أو تقارير مرتبطة بهذا الاحتجاج</p>

                    <div className={styles.storyInputs}>
                        <input type="text" className={styles.input} placeholder="عنوان القصة *"
                            value={newStory.title}
                            onChange={(e) => setNewStory((s) => ({ ...s, title: e.target.value }))} />
                        <RichTextEditor
                            value={newStory.content}
                            onChange={(content) => setNewStory((s) => ({ ...s, content }))}
                            placeholder="محتوى القصة..."
                        />
                        <button type="button" className={styles.addBtn} onClick={addStory}>+ إضافة قصة</button>
                    </div>

                    <div className={styles.list}>
                        {stories.length === 0 && <p className={styles.empty}>لا توجد قصص مضافة</p>}
                        {stories.map((story) => (
                            <div key={story._id} className={styles.listItem}>
                                <div>
                                    <p className={styles.storyTitle}>{story.title}</p>
                                    <p className={styles.storyPreview}
                                        dangerouslySetInnerHTML={{ __html: story.content.slice(0, 100) + '…' }} />
                                </div>
                                <button className={styles.removeBtn}
                                    onClick={() => setStories((s) => s.filter((x) => x._id !== story._id))}>✕</button>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <div className={styles.stepActions}>
                <button type="button" className={styles.backBtn} onClick={() => setStep(1)}>← السابق</button>
                <button type="button" className={styles.submitBtn} onClick={handleSubmit} disabled={loading}>
                    {loading ? 'جاري الحفظ...' : '✓ حفظ الاحتجاج'}
                </button>
            </div>
        </div>
    );
}
